import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
    Search, 
    Filter, 
    ChevronLeft, 
    ChevronRight,
    Edit, 
    Trash2, 
    Eye, 
    RefreshCw,
    User,
    Phone,
    Briefcase,
    Building,
    MapPin,
    BookOpen,
    CheckCircle,
    XCircle,
    AlertCircle,
    Users,
    UserCheck,
    UserX,
    Calendar,
    TrendingUp,
    Award
} from 'lucide-react';

function ViewInspectors() {
    // State for inspectors data
    const [inspectors, setInspectors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Pagination state
    const [meta, setMeta] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0
    });
    
    // Filter and search state
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sortField, setSortField] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [perPage, setPerPage] = useState(10);

    // Create axios instance
    const api = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        timeout: 30000,
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        withCredentials: false
    });

    // Request interceptor to attach token
    api.interceptors.request.use(
        (config) => {
            const token = localStorage.getItem('token');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    // Fetch inspectors
    const fetchInspectors = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await api.get('/viewinspectors');
            const inspectorsData = response.data.inspectors ||response.data.inspections|| [];

            // Apply client-side filtering
            let filteredData = [...inspectorsData];
            
            if (searchTerm) {
                filteredData = filteredData.filter(inspector => 
                    inspector.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    inspector.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    inspector.fieldofstudy?.toLowerCase().includes(searchTerm.toLowerCase())
                );
            }
            
            if (statusFilter) {
                filteredData = filteredData.filter(inspector => 
                    (inspector.status || 'active') === statusFilter
                );
            }
            
            // Apply sorting
            filteredData.sort((a, b) => {
                let aVal = a[sortField];
                let bVal = b[sortField];
                
                if (sortField === 'created_at') {
                    aVal = new Date(aVal);
                    bVal = new Date(bVal);
                }
                
                if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            });
            
            setInspectors(filteredData);
            setMeta({
                current_page: 1,
                last_page: Math.ceil(filteredData.length / perPage),
                per_page: perPage,
                total: filteredData.length
            });
        } catch (err) {
            console.error('Error fetching inspectors:', err);
            setError(err.response?.data?.message || err.message || 'Failed to load inspectors');
            Swal.fire({
                title: 'Error!',
                text: err.response?.data?.message || 'Failed to load inspectors',
                icon: 'error',
                confirmButtonColor: '#4F46E5'
            });
        } finally {
            setLoading(false);
        }
    }, [searchTerm, statusFilter, sortField, sortDirection, perPage]);

    // Initial fetch
    useEffect(() => {
        fetchInspectors();
    }, [fetchInspectors]);

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= meta.last_page) {
            setMeta(prev => ({ ...prev, current_page: newPage }));
        }
    };

    // Handle search change
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setMeta(prev => ({ ...prev, current_page: 1 }));
    };

    // Handle status filter
    const handleStatusChange = (e) => {
        setStatusFilter(e.target.value);
        setMeta(prev => ({ ...prev, current_page: 1 }));
    };

    // Handle sort
    const handleSort = (field) => {
        if (field === sortField) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setMeta(prev => ({ ...prev, current_page: 1 }));
    };

    // Handle per page change
    const handlePerPageChange = (e) => {
        setPerPage(Number(e.target.value));
        setMeta(prev => ({ ...prev, current_page: 1 }));
    };

    // Handle delete
    const handleDelete = async (id, name) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Delete inspector "${name}"? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#EF4444',
            cancelButtonColor: '#6B7280',
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
            background: '#fff',
            customClass: {
                popup: 'rounded-xl',
                confirmButton: 'bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg',
                cancelButton: 'bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded-lg'
            }
        });

        if (result.isConfirmed) {
            try {
                const response = await api.delete(`/inspectors/${id}`);
                
                if (response.data.success) {
                    Swal.fire({
                        title: 'Deleted!',
                        text: 'Inspector deleted successfully',
                        icon: 'success',
                        timer: 2000,
                        timerProgressBar: true,
                        confirmButtonColor: '#4F46E5'
                    });
                    fetchInspectors();
                } else {
                    throw new Error(response.data.message || 'Delete failed');
                }
            } catch (err) {
                Swal.fire({
                    title: 'Error!',
                    text: err.response?.data?.message || 'Failed to delete inspector',
                    icon: 'error',
                    confirmButtonColor: '#EF4444'
                });
            }
        }
    };

    // Handle toggle status
    const handleToggleStatus = async (id, currentStatus) => {
        try {
            const response = await api.patch(`/inspectors/${id}/toggle-status`);
            
            if (response.data.success) {
                Swal.fire({
                    title: 'Success!',
                    text: `Inspector status changed to ${response.data.status}`,
                    icon: 'success',
                    timer: 2000,
                    timerProgressBar: true,
                    confirmButtonColor: '#4F46E5'
                });
                fetchInspectors();
            }
        } catch (err) {
            Swal.fire({
                title: 'Error!',
                text: err.response?.data?.message || 'Failed to update status',
                icon: 'error',
                confirmButtonColor: '#EF4444'
            });
        }
    };

    // Handle view
    const handleView = (inspector) => {
        Swal.fire({
            title: 'Inspector Details',
            html: `
                <div class="text-left">
                    <p><strong>Name:</strong> ${inspector.name}</p>
                    <p><strong>Phone:</strong> ${inspector.phone}</p>
                    <p><strong>Field of Study:</strong> ${inspector.fieldofstudy}</p>
                    <p><strong>Position:</strong> ${inspector.position}</p>
                    <p><strong>Office:</strong> ${inspector.office}</p>
                    <p><strong>Address:</strong> ${inspector.currenaddress}</p>
                    <p><strong>Status:</strong> ${inspector.status || 'active'}</p>
                </div>
            `,
            icon: 'info',
            confirmButtonColor: '#4F46E5',
            customClass: {
                popup: 'rounded-xl'
            }
        });
    };

    // Handle edit
    const handleEdit = (id) => {
        window.location.href = `/admin/editinspector/${id}`;
    };

    // Calculate statistics
    const stats = {
        total: inspectors.length,
        active: inspectors.filter(i => (i.status || 'active') === 'active').length,
        inactive: inspectors.filter(i => (i.status || 'active') === 'inactive').length,
        newThisMonth: inspectors.filter(i => {
            const createdDate = new Date(i.created_at);
            const currentDate = new Date();
            return createdDate.getMonth() === currentDate.getMonth() && 
                   createdDate.getFullYear() === currentDate.getFullYear();
        }).length,
        today: inspectors.filter(i => {
            const createdDate = new Date(i.created_at);
            const today = new Date();
            return createdDate.toDateString() === today.toDateString();
        }).length,
        recent: inspectors.filter(i => {
            const daysAgo = (new Date() - new Date(i.created_at)) / (1000 * 60 * 60 * 24);
            return daysAgo <= 7;
        }).length
    };

    // Status badge component
    const StatusBadge = ({ status }) => {
        const isActive = status === 'active';
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
            }`}>
                {isActive ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                {status || 'active'}
            </span>
        );
    };

    // Get paginated data
    const paginatedInspectors = inspectors.slice(
        (meta.current_page - 1) * perPage,
        meta.current_page * perPage
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <div className="flex items-center gap-3">
                                <Users className="h-10 w-10 text-indigo-600" />
                                <div>
                                    <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                        Inspectors Management
                                    </h1>
                                    <p className="text-gray-600 mt-2">View and manage all registered inspectors</p>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => window.location.href = '/admin/addinspector'}
                            className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-indigo-700 transform hover:scale-105 transition-all duration-200 flex items-center gap-2 shadow-lg"
                        >
                            <User className="h-5 w-5" />
                            Add Inspector
                        </button>
                    </div>
                </div>

                {/* Statistics Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-indigo-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm font-medium">Total Inspectors</p>
                                <p className="text-3xl font-bold text-gray-800 mt-1">{stats.total}</p>
                            </div>
                            <div className="bg-indigo-100 rounded-full p-3">
                                <Users className="h-6 w-6 text-indigo-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-green-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm font-medium">Active Inspectors</p>
                                <p className="text-3xl font-bold text-gray-800 mt-1">{stats.active}</p>
                                <p className="text-xs text-green-600 mt-1">{((stats.active / stats.total) * 100 || 0).toFixed(1)}% of total</p>
                            </div>
                            <div className="bg-green-100 rounded-full p-3">
                                <UserCheck className="h-6 w-6 text-green-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-red-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm font-medium">Inactive Inspectors</p>
                                <p className="text-3xl font-bold text-gray-800 mt-1">{stats.inactive}</p>
                                <p className="text-xs text-red-600 mt-1">{((stats.inactive / stats.total) * 100 || 0).toFixed(1)}% of total</p>
                            </div>
                            <div className="bg-red-100 rounded-full p-3">
                                <UserX className="h-6 w-6 text-red-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-blue-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm font-medium">New This Month</p>
                                <p className="text-3xl font-bold text-gray-800 mt-1">{stats.newThisMonth}</p>
                                <p className="text-xs text-blue-600 mt-1">Recently joined</p>
                            </div>
                            <div className="bg-blue-100 rounded-full p-3">
                                <Calendar className="h-6 w-6 text-blue-600" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Second Row Statistics */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                    <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-purple-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm font-medium">Today's Joins</p>
                                <p className="text-3xl font-bold text-gray-800 mt-1">{stats.today}</p>
                                <p className="text-xs text-purple-600 mt-1">{new Date().toLocaleDateString()}</p>
                            </div>
                            <div className="bg-purple-100 rounded-full p-3">
                                <TrendingUp className="h-6 w-6 text-purple-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-teal-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm font-medium">Recent Joins</p>
                                <p className="text-3xl font-bold text-gray-800 mt-1">{stats.recent}</p>
                                <p className="text-xs text-teal-600 mt-1">Last 7 days</p>
                            </div>
                            <div className="bg-teal-100 rounded-full p-3">
                                <Award className="h-6 w-6 text-teal-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-orange-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm font-medium">Active Rate</p>
                                <p className="text-3xl font-bold text-gray-800 mt-1">
                                    {stats.total > 0 ? ((stats.active / stats.total) * 100).toFixed(0) : 0}%
                                </p>
                                <p className="text-xs text-orange-600 mt-1">of total inspectors</p>
                            </div>
                            <div className="bg-orange-100 rounded-full p-3">
                                <CheckCircle className="h-6 w-6 text-orange-600" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters and Search */}
                <div className="bg-indigo-700 rounded-lg p-4 mb-6 shadow-lg">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-600 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search by name, phone..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="w-full pl-10 pr-4 py-2 border-2 border-indigo-300 rounded-lg focus:outline-none focus:border-indigo-500 bg-white"
                            />
                        </div>

                        {/* Status Filter */}
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-600 w-5 h-5" />
                            <select
                                value={statusFilter}
                                onChange={handleStatusChange}
                                className="w-full pl-10 pr-4 py-2 border-2 border-indigo-300 rounded-lg focus:outline-none focus:border-indigo-500 appearance-none bg-white"
                            >
                                <option value="">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>

                        {/* Per Page */}
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-600 font-medium">Show</span>
                            <select
                                value={perPage}
                                onChange={handlePerPageChange}
                                className="w-full pl-16 pr-4 py-2 border-2 border-indigo-300 rounded-lg focus:outline-none focus:border-indigo-500 appearance-none bg-white"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>

                        {/* Refresh Button */}
                        <div>
                            <button
                                onClick={() => fetchInspectors()}
                                className="w-full bg-white text-indigo-700 hover:bg-indigo-50 font-medium py-2 px-4 rounded-lg border-2 border-white flex items-center justify-center transition duration-200"
                            >
                                <RefreshCw className="w-5 h-5 mr-2" />
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-indigo-700">
                                <tr>
                                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer hover:bg-indigo-600 transition-colors" onClick={() => handleSort('name')}>
                                        <div className="flex items-center">
                                            <User className="w-4 h-4 mr-1" />
                                            Name
                                            {sortField === 'name' && (
                                                <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                            )}
                                        </div>
                                    </th>
                                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer hover:bg-indigo-600 transition-colors" onClick={() => handleSort('phone')}>
                                        <div className="flex items-center">
                                            <Phone className="w-4 h-4 mr-1" />
                                            Phone
                                            {sortField === 'phone' && (
                                                <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                            )}
                                        </div>
                                    </th>
                                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                        <div className="flex items-center">
                                            <BookOpen className="w-4 h-4 mr-1" />
                                            Field of Study
                                        </div>
                                    </th>
                                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                        <div className="flex items-center">
                                            <Briefcase className="w-4 h-4 mr-1" />
                                            Position
                                        </div>
                                    </th>
                                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                        <div className="flex items-center">
                                            <Building className="w-4 h-4 mr-1" />
                                            Office
                                        </div>
                                    </th>
                                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer hover:bg-indigo-600 transition-colors" onClick={() => handleSort('status')}>
                                        <div className="flex items-center">
                                            Status
                                            {sortField === 'status' && (
                                                <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                            )}
                                        </div>
                                    </th>
                                    <th scope="col" className="px-6 py-4 text-right text-xs font-medium text-white uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-8 text-center">
                                            <div className="flex justify-center items-center">
                                                <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <span className="ml-3 text-gray-600">Loading inspectors...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : error ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-8 text-center">
                                            <AlertCircle className="w-12 h-12 mx-auto mb-2 text-red-500" />
                                            <p className="text-red-600">{error}</p>
                                            <button
                                                onClick={() => fetchInspectors()}
                                                className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
                                            >
                                                Try Again
                                            </button>
                                        </td>
                                    </tr>
                                ) : paginatedInspectors.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-8 text-center">
                                            <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                                            <p className="text-gray-500">No inspectors found.</p>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedInspectors.map((inspector) => (
                                        <tr key={inspector.id} className="hover:bg-indigo-50 transition duration-150">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {inspector.name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {inspector.phone}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {inspector.fieldofstudy}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {inspector.position}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {inspector.office}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <StatusBadge status={inspector.status || 'active'} />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end space-x-2">
                                                    <button
                                                        onClick={() => handleView(inspector)}
                                                        className="p-1 text-indigo-600 hover:text-indigo-800 transition rounded-lg hover:bg-indigo-100"
                                                        title="View Details"
                                                    >
                                                        <Eye className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEdit(inspector.id)}
                                                        className="p-1 text-blue-600 hover:text-blue-800 transition rounded-lg hover:bg-blue-100"
                                                        title="Edit"
                                                    >
                                                        <Edit className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleToggleStatus(inspector.id, inspector.status)}
                                                        className={`p-1 transition rounded-lg ${
                                                            (inspector.status || 'active') === 'active' 
                                                                ? 'text-red-600 hover:text-red-800 hover:bg-red-100' 
                                                                : 'text-green-600 hover:text-green-800 hover:bg-green-100'
                                                        }`}
                                                        title={(inspector.status || 'active') === 'active' ? 'Deactivate' : 'Activate'}
                                                    >
                                                        {(inspector.status || 'active') === 'active' ? <XCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(inspector.id, inspector.name)}
                                                        className="p-1 text-red-600 hover:text-red-800 transition rounded-lg hover:bg-red-100"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {!loading && !error && meta.last_page > 1 && (
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="text-sm text-gray-700">
                                Showing <span className="font-medium">{((meta.current_page - 1) * perPage) + 1}</span> to{' '}
                                <span className="font-medium">{Math.min(meta.current_page * perPage, meta.total)}</span> of{' '}
                                <span className="font-medium">{meta.total}</span> results
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => handlePageChange(meta.current_page - 1)}
                                    disabled={meta.current_page === 1}
                                    className="px-3 py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <span className="px-4 py-1 bg-indigo-600 text-white rounded-lg">
                                    Page {meta.current_page} of {meta.last_page}
                                </span>
                                <button
                                    onClick={() => handlePageChange(meta.current_page + 1)}
                                    disabled={meta.current_page === meta.last_page}
                                    className="px-3 py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ViewInspectors;